import { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Gauge, ArrowUp, ArrowDown, Minus } from "lucide-react";

export const HEALTH_COLORS = {
  "Financial Health": "#c9a961",
  "Operations": "#38bdf8",
  "Sales": "#f472b6",
  "Marketing": "#a78bfa",
  "Technology": "#ef4444",
  "Customer Experience": "#34d399",
  "Leadership": "#fbbf24",
  "Growth Potential": "#10b981",
};

// trend: [{ id, created_at, scores: [{category, score}] }] sorted oldest → newest
export default function HealthScoreTrend({ trend, dark = false }) {
  const { rows, categories, latest, prev } = useMemo(() => {
    const pts = (trend || []).filter((t) => (t.scores || []).length > 0);
    const cats = [];
    for (const t of pts) for (const s of t.scores) if (!cats.includes(s.category)) cats.push(s.category);
    const rows = pts.map((t) => {
      const row = { date: new Date(t.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) };
      for (const s of t.scores) row[s.category] = s.score;
      return row;
    });
    const scoreMap = (t) => Object.fromEntries((t?.scores || []).map((s) => [s.category, s.score]));
    return {
      rows,
      categories: cats,
      latest: pts.length ? scoreMap(pts[pts.length - 1]) : null,
      prev: pts.length > 1 ? scoreMap(pts[pts.length - 2]) : null,
    };
  }, [trend]);

  if (!latest) return null;

  const ink = dark ? "text-white" : "text-[#1e3d3a]";
  const sub = dark ? "text-white/50" : "text-[#1e3d3a]/50";
  const track = dark ? "bg-white/10" : "bg-[#1e3d3a]/10";
  const gridStroke = dark ? "rgba(255,255,255,0.06)" : "rgba(30,61,58,0.08)";
  const tickFill = dark ? "rgba(255,255,255,0.4)" : "rgba(30,61,58,0.45)";

  return (
    <div data-testid="health-score-trend">
      <span className={`text-[11px] uppercase tracking-[0.18em] ${sub} flex items-center gap-1.5`}>
        <Gauge className="h-3.5 w-3.5" /> Business health trend
      </span>

      {rows.length >= 2 && (
        <div className="mt-3 h-56" data-testid="health-trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
              <XAxis dataKey="date" tick={{ fill: tickFill, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} tick={{ fill: tickFill, fontSize: 10 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: "#1e3d3a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 10 }} iconType="circle" iconSize={7} />
              {categories.map((c) => (
                <Line key={c} type="monotone" dataKey={c} stroke={HEALTH_COLORS[c] || "#c9a961"}
                  strokeWidth={2} dot={{ r: 2.5 }} isAnimationActive={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="mt-4 space-y-2" data-testid="health-trend-latest">
        {categories.map((c) => {
          const cur = latest[c];
          if (cur === undefined) return null;
          const before = prev?.[c];
          const delta = before === undefined ? null : cur - before;
          const DeltaIcon = delta === null || delta === 0 ? Minus : delta > 0 ? ArrowUp : ArrowDown;
          const deltaColor = delta === null || delta === 0 ? (dark ? "text-white/35" : "text-[#1e3d3a]/35") : delta > 0 ? "text-[#10b981]" : "text-[#ef4444]";
          return (
            <div key={c} className="flex items-center gap-3 text-xs">
              <span className={`w-36 shrink-0 truncate ${dark ? "text-white/70" : "text-[#1e3d3a]/70"}`}>{c}</span>
              <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${track}`}>
                <div className="h-full rounded-full transition-[width] duration-700"
                  style={{ width: `${cur}%`, background: HEALTH_COLORS[c] || "#c9a961" }} />
              </div>
              <span className={`w-12 text-right tabular-nums font-semibold ${ink}`}>{cur}/100</span>
              <span className={`w-10 inline-flex items-center gap-0.5 tabular-nums ${deltaColor}`}>
                <DeltaIcon className="h-3 w-3" />{delta === null ? "" : Math.abs(delta)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
